import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import { useRenderOperations } from '@/features/design/render/hooks/use-render-operations';
import {
  useCreateConcept,
  useUpdateConcept,
  useDeleteAngle,
} from './use-concepts';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { Project } from '@/types/project';
import type { Concept, ConceptAngle } from '@/types/concept';

interface UseConceptActionsProps {
  project: Project;
  concept: Concept;
  // Current concept angles, ordered. Needed so single-angle mutations (delete,
  // undo-restore) can build a full sync payload without re-fetching.
  conceptAngles: ConceptAngle[];
  isArchived: boolean;
  onRefetch: () => void;
  // Invoked after a successful undo-restore of a deleted derivative angle, so
  // the caller can surface the restored angle (e.g. switch the active tab).
  onAngleRestored?: (projectAngleId: string) => void;
}

export function useConceptActions({
  project,
  concept,
  conceptAngles,
  isArchived,
  onRefetch,
  onAngleRestored,
}: UseConceptActionsProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const setError = useConceptStore((state) => state.setError);
  const removeSelectedRenderId = useConceptStore(
    (state) => state.removeSelectedRenderId
  );

  const createConceptMutation = useCreateConcept();
  const updateConceptMutation = useUpdateConcept();
  const deleteAngleMutation = useDeleteAngle();
  const { deleteRender } = useRenderOperations();

  const createConceptFromAngle = useCallback(
    async (angleId: string) => {
      if (isArchived) return;

      try {
        const newConcept = await createConceptMutation.mutateAsync({
          projectId: project.id,
          mainAngleId: angleId,
        });
        router.push(`/project/${project.id}/concept/${newConcept.id}`);
      } catch (_error) {
        setError('Failed to create concept');
      }
    },
    [createConceptMutation, project.id, router, isArchived, setError]
  );

  const handleAngleUploadSuccess = useCallback(async () => {
    setError(null);
    // Only invalidate angles list - renders are handled by WebSocket and Store
    queryClient.invalidateQueries({
      queryKey: cacheKeys.concepts.angles(concept?.id),
    });
    await onRefetch(); // Still needed for angles and concept metadata
  }, [concept?.id, queryClient, onRefetch, setError]);

  const handleDeleteAngle = useCallback(
    async (conceptAngleId: string, angleId: string | undefined) => {
      if (!concept || !angleId) return;

      try {
        const currentAngleIds = conceptAngles
          .map((ca) => ca.relationships?.angle?.data?.id)
          .filter((id): id is string => !!id);
        await deleteAngleMutation.mutateAsync({
          conceptId: concept.id,
          currentAngleIds,
          angleId,
          onAngleRestored,
        });
        // Clean up orphaned carousel selection for the deleted angle
        removeSelectedRenderId(conceptAngleId);
        queryClient.invalidateQueries({
          queryKey: cacheKeys.concepts.angles(concept.id),
        });
        await onRefetch();
      } catch (_error) {
        setError('Failed to delete angle');
      }
    },
    [
      concept,
      conceptAngles,
      deleteAngleMutation,
      queryClient,
      onRefetch,
      setError,
      removeSelectedRenderId,
    ]
  );

  const handleDeleteRender = useCallback(
    async (renderId: string) => {
      if (!concept) return;

      try {
        await deleteRender(renderId);
        // NO onRefetch needed - Store handles render deletion via mutation
      } catch (_error) {
        setError('Failed to delete render');
      }
    },
    [concept, deleteRender, setError]
  );

  const handleNoteUpdate = useCallback(
    (note: string) => {
      if (!concept || isArchived) return;

      updateConceptMutation.mutate({
        projectId: project.id,
        conceptId: concept.id,
        updates: {
          title: concept.attributes.title || '',
          note,
        },
      });
    },
    [concept, isArchived, updateConceptMutation, project.id]
  );

  return {
    createConceptFromAngle,
    handleAngleUploadSuccess,
    handleDeleteAngle,
    handleDeleteRender,
    handleNoteUpdate,
  };
}
